<template>
    <div class="v-datetime" :class="{ 'disabled': disabled }">
        <p v-if="label" class="label">{{ label }}</p>
        <div class="datetime-wrapper" :class="{ 'open': isOpen, 'error': isError }" @click="toggleDropdown"
            ref="datetimeWrapper" v-click-outside="closeDropdown"
            :style="{ 'min-width': minWidth, 'max-width': maxWidth }">
            <div class="selected-value">
                <span v-if="modelValue" class="text">{{ formatDateTime(modelValue) }}</span>
                <span v-else class="placeholder">{{ placeholder }}</span>
            </div>
            <simple-svg :src="CalendarIcon" :custom-class-name="'icon calendar-icon'" />

            <teleport to="body">
            <div class="datetime-picker" v-if="isOpen" :style="pickerStyle">
                <div class="picker-header">
                    <button @click.stop="prevMonth" class="nav-btn">←</button>
                    <div class="month-year">{{ monthYearDisplay }}</div>
                    <button @click.stop="nextMonth" class="nav-btn">→</button>
                </div>

                <div class="calendar">
                    <div class="weekdays">
                        <div v-for="day in weekDays" :key="day" class="weekday">{{ day }}</div>
                    </div>
                    <div class="days">
                        <div v-for="day in calendarDays" :key="day.fullDate.getTime()" :class="{
                            'other-month': !day.isCurrentMonth,
                            'selected': isDateSelected(day),
                            'in-range': isInRange(day),
                            'today': isToday(day)
                        }" class="day" @click.stop="selectDate(day)">
                            {{ day.date }}
                        </div>
                    </div>
                </div>

                <div class="time-picker" v-if="enableTime">
                    <div class="time-input">
                        <label>Часы</label>
                        <input v-model.number="selectedHour" type="number" min="0" max="23" @click.stop>
                    </div>
                    <div class="separator">:</div>
                    <div class="time-input">
                        <label>Минуты</label>
                        <input v-model.number="selectedMinute" type="number" min="0" max="59" @click.stop>
                    </div>
                </div>

                <div class="picker-footer">
                    <button @click.stop="clearDateTime" class="btn-clear">Очистить</button>
                    <button @click.stop="confirmDateTime" class="btn-confirm">OK</button>
                </div>
            </div>
            </teleport>
        </div>
    </div>
</template>

<script>
import CalendarIcon from '@/assets/icons/Calendar.svg';

export default {
    name: 'VDateTime',
    props: {
        modelValue: {
            type: [String, Date, Array, null],
            default: null
        },
        label: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: 'Выберите дату и время'
        },
        minWidth: {
            type: String,
            default: '200px'
        },
        maxWidth: {
            type: String,
            default: '250px'
        },
        isError: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        enableTime: {
            type: Boolean,
            default: true
        },
        isRange: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue', 'change'],
    data() {
        return {
            isOpen: false,
            CalendarIcon,
            currentDate: new Date(),
            selectedDate: null,
            selectedHour: 0,
            selectedMinute: 0,
            weekDays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
            openUp: false,
            rangeStart: null,
            rangeEnd: null,
            pickerStyle: {}
        }
    },
    computed: {
        monthYearDisplay() {
            const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
                'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
            return `${months[this.currentDate.getMonth()]} ${this.currentDate.getFullYear()}`;
        },
        calendarDays() {
            const year = this.currentDate.getFullYear();
            const month = this.currentDate.getMonth();
            const firstDay = new Date(year, month, 1);
            const lastDay = new Date(year, month + 1, 0);

            const days = [];

            // Fix start date calculation for Monday start
            let dayOfWeek = firstDay.getDay(); // 0 (Sun) - 6 (Sat)
            if (dayOfWeek === 0) dayOfWeek = 7; // Convert to 1 (Mon) - 7 (Sun)

            const startDate = new Date(firstDay);
            startDate.setDate(startDate.getDate() - dayOfWeek + 1);

            let date = new Date(startDate);
            while (date <= lastDay || date.getDay() !== 1) {
                days.push({
                    date: date.getDate(),
                    fullDate: new Date(date),
                    isCurrentMonth: date.getMonth() === month
                });
                date.setDate(date.getDate() + 1);
            }

            return days;
        }
    },
    methods: {
        toggleDropdown() {
            if (!this.disabled) {
                this.isOpen = !this.isOpen;
                if (this.isOpen) {
                    this.calculatePosition();
                    this.initializeDateTime();
                }
            }
        },
        calculatePosition() {
            this.$nextTick(() => {
                const wrapper = this.$refs.datetimeWrapper;
                if (wrapper) {
                    const rect = wrapper.getBoundingClientRect();
                    const windowHeight = window.innerHeight;
                    const pickerHeight = 460;

                    const spaceBelow = windowHeight - rect.bottom;
                    const spaceAbove = rect.top;
                    this.openUp = spaceBelow < pickerHeight && spaceAbove > spaceBelow;

                    if (this.openUp) {
                        this.pickerStyle = {
                            position: 'fixed',
                            bottom: `${windowHeight - rect.top + 4}px`,
                            left: `${rect.left}px`,
                            zIndex: 19999
                        };
                    } else {
                        this.pickerStyle = {
                            position: 'fixed',
                            top: `${rect.bottom + 4}px`,
                            left: `${rect.left}px`,
                            zIndex: 19999
                        };
                    }
                }
            });
        },
        closeDropdown() {
            this.isOpen = false;
        },
        initializeDateTime() {
            let isValidDate = false;

            if (this.modelValue) {
                if (this.isRange && Array.isArray(this.modelValue) && this.modelValue.length === 2 && this.modelValue[0] && this.modelValue[1]) {
                    const start = new Date(this.modelValue[0]);
                    const end = new Date(this.modelValue[1]);
                    if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
                        this.rangeStart = start;
                        this.rangeEnd = end;
                        this.currentDate = new Date(start.getFullYear(), start.getMonth(), 1);
                        isValidDate = true;
                    }
                } else if (!Array.isArray(this.modelValue)) {
                    const date = new Date(this.modelValue);
                    if (!isNaN(date.getTime())) {
                        this.currentDate = new Date(date.getFullYear(), date.getMonth(), 1);
                        this.selectedDate = new Date(date);
                        this.selectedHour = date.getHours();
                        this.selectedMinute = date.getMinutes();
                        isValidDate = true;
                    }
                }
            }

            if (!isValidDate) {
                this.currentDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
                this.selectedDate = null;
                this.rangeStart = null;
                this.rangeEnd = null;
                this.selectedHour = 0;
                this.selectedMinute = 0;
            }
        },
        prevMonth() {
            this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
        },
        nextMonth() {
            this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
        },
        selectDate(day) {
            if (this.isRange) {
                const date = new Date(day.fullDate);
                // Clear time part for accurate comparison
                date.setHours(0, 0, 0, 0);

                if (!this.rangeStart || (this.rangeStart && this.rangeEnd)) {
                    this.rangeStart = date;
                    this.rangeEnd = null;
                } else if (this.rangeStart && !this.rangeEnd) {
                    if (date < this.rangeStart) {
                        this.rangeEnd = this.rangeStart;
                        this.rangeStart = date;
                    } else {
                        this.rangeEnd = date;
                    }
                }
            } else {
                this.selectedDate = new Date(day.fullDate);
            }
        },
        isDateSelected(day) {
            if (this.isRange) {
                if (!this.rangeStart) return false;
                const date = day.fullDate.getTime();
                const start = this.rangeStart.getTime();
                const end = this.rangeEnd ? this.rangeEnd.getTime() : null;
                return date === start || date === end;
            } else {
                if (!this.selectedDate) return false;
                return day.fullDate.toDateString() === this.selectedDate.toDateString();
            }
        },
        isInRange(day) {
            if (!this.isRange || !this.rangeStart || !this.rangeEnd) return false;
            const date = day.fullDate.getTime();
            return date > this.rangeStart.getTime() && date < this.rangeEnd.getTime();
        },
        isRangeStart(day) {
            if (!this.isRange || !this.rangeStart) return false;
            return day.fullDate.toDateString() === this.rangeStart.toDateString();
        },
        isRangeEnd(day) {
            if (!this.isRange || !this.rangeEnd) return false;
            return day.fullDate.toDateString() === this.rangeEnd.toDateString();
        },
        isToday(day) {
            return day.fullDate.toDateString() === new Date().toDateString();
        },
        confirmDateTime() {
            if (this.isRange) {
                if (this.rangeStart && this.rangeEnd) {
                    // Apply time to start/end if enabled? For now, keep as dates 00:00 if enableTime is false or ambiguous. 
                    // Let's support time for range if enableTime is true? It's complex. 
                    // Assuming enableTime applies to both or just start? The user asked to DISABLE time mostly or SELECT interval.
                    // Let's respect enableTime for Start date, and maybe end date? 
                    // To keep it simple: if isRange, we send the start/end pair with existing hours?
                    // Actually, if enableTime is used with range, we might just ignore specific time input for now as generic requirement "select interval" usually implies dates.
                    // But to be safe, let's just clamp to 00:00 usually for ranges unless specialized.

                    const start = new Date(this.rangeStart);
                    const end = new Date(this.rangeEnd);

                    if (this.enableTime) {
                        // If we wanted to support time in range, we'd need dual time pickers. 
                        // For now, let's just default to start of day and end of day? Or preserve selectedHour?
                        start.setHours(this.selectedHour, this.selectedMinute, 0, 0);
                        end.setHours(23, 59, 59, 999); // Typical "Include all of end day" behavior? Or same time?
                        // Let's stick to what we know: User asked to disable time usually.
                    } else {
                        start.setHours(0, 0, 0, 0);
                        end.setHours(0, 0, 0, 0); // Or 23:59:59? Usually backend handles inclusive/exclusive. Let's send start of day.
                    }

                    this.$emit('update:modelValue', [start.toISOString(), end.toISOString()]);
                    this.$emit('change', [start, end]);
                    this.closeDropdown();
                }
            } else {
                if (this.selectedDate) {
                    const dateTime = new Date(this.selectedDate);
                    if (this.enableTime) {
                        dateTime.setHours(this.selectedHour, this.selectedMinute, 0, 0);
                    } else {
                        dateTime.setHours(0, 0, 0, 0);
                    }
                    this.$emit('update:modelValue', dateTime.toISOString());
                    this.$emit('change', dateTime);
                    this.closeDropdown();
                }
            }
        },
        clearDateTime() {
            this.$emit('update:modelValue', null);
            this.$emit('change', null);
            this.closeDropdown();
        },
        formatDateTime(value) {
            if (!value) return '';

            if (Array.isArray(value) && value.length === 2) {
                const start = this.formatSingleDate(value[0]);
                const end = this.formatSingleDate(value[1]);
                return `${start} - ${end}`;
            }

            return this.formatSingleDate(value);
        },
        formatSingleDate(value) {
            if (!value) return '';
            const date = new Date(value);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();

            if (!this.enableTime) {
                return `${day}.${month}.${year}`;
            }

            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${day}.${month}.${year} ${hours}:${minutes}`;
        }
    },
    directives: {
        'click-outside': {
            mounted(el, binding) {
                el.clickOutsideEvent = function (event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.body.addEventListener('click', el.clickOutsideEvent);
            },
            unmounted(el) {
                document.body.removeEventListener('click', el.clickOutsideEvent);
            },
        },
    }
}
</script>

<style lang="scss" scoped>
.v-datetime {
    .label {
        font-weight: 600;
        font-size: 14px;
        margin-bottom: 5px;
        color: var(--color-text);
    }

    &.disabled {
        opacity: 0.6;
        pointer-events: none;
    }
}

.datetime-wrapper {
    position: relative;
    box-sizing: border-box;
    background-color: var(--color-input-bg);
    border: solid 1px var(--color-border);
    border-radius: 5px;
    padding: 6px 8px;
    color: var(--color-input-text);
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 29px;

    &:hover {
        border-color: var(--color-input-focus);
    }

    &.open {
        border: solid 1px var(--color-input-focus);
        box-shadow: 0 0 6px var(--color-input-focus);

        .calendar-icon {
            transform: scale(1.1);
        }
    }

    &.error {
        box-shadow: 0 0 6px var(--color-input-error);
    }

    .selected-value {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        .placeholder {
            color: var(--color-text-light);
        }
    }

    .calendar-icon {
        width: 16px;
        height: 16px;
        transition: transform 0.2s ease;

        path {
            fill: var(--color-text-light);
        }
    }
}

.datetime-picker {
    background-color: var(--color-bg);
    border: 1px solid var(--color-border);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    padding: 12px;
    min-width: 320px;

    .picker-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 12px;

        .nav-btn {
            background: none;
            border: none;
            cursor: pointer;
            color: var(--color-text);
            font-size: 18px;
            padding: 4px 8px;
            transition: color 0.2s;

            &:hover {
                color: var(--color-btn);
            }
        }

        .month-year {
            font-weight: 600;
            color: var(--color-text);
            min-width: 150px;
            text-align: center;
        }
    }

    .calendar {
        margin-bottom: 12px;

        .weekdays {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            gap: 4px;
            margin-bottom: 4px;

            .weekday {
                text-align: center;
                font-size: 12px;
                font-weight: 600;
                color: var(--color-text-light);
                padding: 4px;
            }
        }

        .days {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            gap: 4px;

            .day {
                aspect-ratio: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 4px;
                cursor: pointer;
                transition: background-color 0.2s;
                color: var(--color-text);
                font-size: 14px;
                position: relative; // For better stacking of range styles

                &.other-month {
                    color: var(--color-text-light);
                    opacity: 0.5;
                }

                &:not(.other-month):hover {
                    background-color: color-mix(in srgb, var(--color-btn) 20%, transparent);
                }

                &.selected {
                    background-color: var(--color-btn);
                    color: white;
                    font-weight: 600;
                }

                &.in-range {
                    background-color: color-mix(in srgb, var(--color-btn) 15%, transparent);
                    color: var(--color-text);
                }

                &.today {
                    border: 2px solid var(--color-btn);
                }
            }
        }
    }

    .time-picker {
        display: flex;
        align-items: flex-end;
        gap: 8px;
        margin-bottom: 12px;

        .time-input {
            display: flex;
            flex-direction: column;
            align-items: center;
            flex: 1;

            label {
                font-size: 12px;
                color: var(--color-text-light);
                margin-bottom: 4px;
            }

            input {
                width: 100%;
                padding: 4px 6px;
                border: 1px solid var(--color-border);
                border-radius: 4px;
                text-align: center;
                font-size: 14px;
                color: var(--color-text);
                background-color: var(--color-input-bg);

                &:focus {
                    outline: none;
                    border-color: var(--color-input-focus);
                    box-shadow: 0 0 4px var(--color-input-focus);
                }
            }
        }

        .separator {
            font-size: 18px;
            font-weight: 600;
            color: var(--color-text);
            margin-bottom: 2px;
        }
    }

    .picker-footer {
        display: flex;
        gap: 8px;
        justify-content: flex-end;

        button {
            padding: 6px 12px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.2s;

            &.btn-clear {
                background-color: transparent;
                border: 1px solid var(--color-border);
                color: var(--color-text);

                &:hover {
                    background-color: color-mix(in srgb, var(--color-text) 10%, transparent);
                }
            }

            &.btn-confirm {
                background-color: var(--color-btn);
                color: white;

                &:hover {
                    background-color: var(--color-btn-hover);
                }
            }
        }
    }
}
</style>
